import { formatAddress, isReference, resolveId } from '@medplum/core'
import type { Organization, Reference } from '@medplum/fhirtypes'
import { referenceDisplay } from '@seen/core/display'
import { ExtendedOrganization } from '@seen/core/models/organizations/ExtendedOrganization'
import { cn } from '@seen/ui'
import {
  IconBuilding,
  IconMapPin,
  IconPhone,
  IconPrinter,
} from '@tabler/icons-react'
import type { JSX, ReactNode } from 'react'
import { HoverCard } from '#src/components/core/hover-card/HoverCard.tsx'
import { HoverCardContent } from '#src/components/core/hover-card/HoverCardContent.tsx'
import { HoverCardTrigger } from '#src/components/core/hover-card/HoverCardTrigger.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { DevMedplumAppLinkOverlay } from '#src/components/medplum/DevMedplumAppLinkOverlay.tsx'
import { useRead } from '#src/hooks/useRead.ts'

export interface HoverOrganizationProps {
  organization: ExtendedOrganization | Reference<Organization> | undefined
  className?: string
  /** When true, renders inline without hover card wrapper (for nesting in other hover cards) */
  inline?: boolean
}

interface ContactItemProps {
  icon: ReactNode
  value: string | undefined
  label: string
}

function ContactItem({
  icon,
  value,
  label,
}: ContactItemProps): JSX.Element | null {
  if (!value) {
    return null
  }
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <div className="flex min-w-0 flex-col">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="break-all">{value}</span>
      </div>
    </div>
  )
}

export interface HoverOrganizationContentProps {
  organization: ExtendedOrganization | undefined
  organizationReference: Reference<Organization> | undefined
}

export function HoverOrganizationContent({
  organization,
  organizationReference,
}: HoverOrganizationContentProps): JSX.Element {
  const organizationName =
    organization?.name ??
    referenceDisplay(organizationReference) ??
    'Unknown Organization'
  const organizationId = resolveId(organizationReference)
  const organizationHref = organizationId
    ? `/organizations/${organizationId}`
    : undefined

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="flex size-10 items-center justify-center rounded-full bg-muted">
          <IconBuilding size={20} className="text-muted-foreground" />
        </div>
        <div className="flex min-w-0 flex-col">
          <Link
            to={organizationHref ?? '#'}
            className="font-medium hover:underline"
            onClick={(e) => {
              if (!organizationHref) {
                e.preventDefault()
              }
            }}
          >
            {organizationName}
          </Link>
          <span className="text-sm text-muted-foreground">
            {organization?.active ? 'Active' : 'Inactive'}
          </span>
          {organization && (
            <DevMedplumAppLinkOverlay to={organization} inline />
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <ContactItem
          icon={<IconPhone size={14} />}
          value={organization?.primaryPhoneNumber}
          label="Phone"
        />
        <ContactItem
          icon={<IconPrinter size={14} />}
          value={organization?.primaryFaxNumber}
          label="Fax"
        />
        {organization?.primaryAddress && (
          <ContactItem
            icon={<IconMapPin size={14} />}
            value={formatAddress(organization.primaryAddress)}
            label="Address"
          />
        )}
      </div>
    </div>
  )
}

/** Inline version for nesting inside other hover cards */
interface OrganizationInlineCardProps {
  organization: ExtendedOrganization | undefined
  organizationReference: Reference<Organization> | undefined
  organizationHref: string | undefined
}

function OrganizationInlineCard({
  organization,
  organizationReference,
  organizationHref,
}: OrganizationInlineCardProps): JSX.Element | null {
  const organizationName =
    organization?.name ?? referenceDisplay(organizationReference)

  const hasContactInfo =
    organization?.primaryPhoneNumber ||
    organization?.primaryFaxNumber ||
    organization?.primaryAddress

  if (!hasContactInfo && !organizationName) {
    return null
  }

  return (
    <div className="mt-3 flex flex-col gap-2 border-t pt-3">
      <div className="flex items-center gap-2">
        <IconBuilding size={14} className="text-muted-foreground" />
        <Link
          to={organizationHref ?? '#'}
          className="text-sm font-medium hover:underline"
          onClick={(e) => {
            if (!organizationHref) {
              e.preventDefault()
            }
          }}
        >
          {organizationName}
        </Link>
      </div>
      {organization?.primaryPhoneNumber && (
        <ContactItem
          icon={<IconPhone size={14} />}
          value={organization.primaryPhoneNumber}
          label="Phone"
        />
      )}
      {organization?.primaryFaxNumber && (
        <ContactItem
          icon={<IconPrinter size={14} />}
          value={organization.primaryFaxNumber}
          label="Fax"
        />
      )}
      {organization?.primaryAddress && (
        <ContactItem
          icon={<IconMapPin size={14} />}
          value={formatAddress(organization.primaryAddress)}
          label="Address"
        />
      )}
    </div>
  )
}

export function HoverOrganization({
  organization: organizationOrReference,
  className,
  inline = false,
}: HoverOrganizationProps): JSX.Element | null {
  const { data } = useRead(
    ExtendedOrganization,
    isReference(organizationOrReference) ? organizationOrReference : undefined,
    {
      query: {
        enabled: !(organizationOrReference instanceof ExtendedOrganization),
      },
    }
  )
  const organization =
    organizationOrReference instanceof ExtendedOrganization
      ? organizationOrReference
      : data

  const organizationReference =
    organizationOrReference instanceof ExtendedOrganization
      ? organizationOrReference.reference
      : organizationOrReference

  const organizationId = resolveId(organizationReference)
  const organizationHref = organizationId
    ? `/organizations/${organizationId}`
    : undefined

  const organizationName =
    organization?.name ?? referenceDisplay(organizationReference)

  if (!organizationOrReference) {
    return null
  }

  if (inline) {
    return (
      <OrganizationInlineCard
        organization={organization}
        organizationReference={organizationReference}
        organizationHref={organizationHref}
      />
    )
  }

  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild>
        <Link
          to={organizationHref ?? '#'}
          className={cn(
            'cursor-pointer overflow-hidden text-ellipsis text-nowrap decoration-1 underline-offset-[2.5px] hover:underline',
            className
          )}
          onClick={(e) => {
            if (!organizationHref) {
              e.preventDefault()
            }
          }}
        >
          {organizationName}
        </Link>
      </HoverCardTrigger>
      <HoverCardContent className="w-80" side="bottom" align="start">
        <HoverOrganizationContent
          organization={organization}
          organizationReference={organizationReference}
        />
      </HoverCardContent>
    </HoverCard>
  )
}
